import { ActionIcon, Badge, Card, Checkbox, Group, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import type { ScheduleCategory } from './AblaufplanCategoryManager';
import type { ScheduleEntry } from './AblaufplanEntryModal';

type Props = {
  entries: ScheduleEntry[];
  categories: ScheduleCategory[];
  formId: string;
  highlightedEntryIds?: string[];
  selectionMode?: boolean;
  selectedEntryIds?: string[];
  onToggleSelection?: (entryId: string) => void;
  onEdit: (entry: ScheduleEntry) => void;
  onDelete: (entry: ScheduleEntry) => void;
};

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function stripSeconds(t: string): string {
  return t.slice(0, 5);
}

function groupByDate(entries: ScheduleEntry[]): [string, ScheduleEntry[]][] {
  const map = new Map<string, ScheduleEntry[]>();
  for (const e of entries) {
    const list = map.get(e.entry_date) ?? [];
    list.push(e);
    map.set(e.entry_date, list);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function AblaufplanListView({
  entries,
  categories,
  formId,
  highlightedEntryIds,
  selectionMode = false,
  selectedEntryIds = [],
  onToggleSelection,
  onEdit,
  onDelete,
}: Props) {
  const qc = useQueryClient();
  const categoryMap = new Map(categories.map((c) => [c.id, c]));
  const highlighted = new Set(highlightedEntryIds ?? []);
  const selected = new Set(selectedEntryIds);

  const toggleVisibility = useMutation({
    mutationFn: async ({ entry, visible }: { entry: ScheduleEntry; visible: boolean }) => {
      const { error } = await supabase.from('schedule_entries').update({ is_app_visible: visible }).eq('id', entry.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-entries', formId] }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler', message: err.message }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (entry: ScheduleEntry) => {
      const { data: newRow, error } = await supabase
        .from('schedule_entries')
        .insert({
          form_id: entry.form_id,
          category_id: entry.category_id,
          entry_date: entry.entry_date,
          start_time: entry.start_time,
          end_time: entry.end_time,
          title: `${entry.title} (Kopie)`,
          description: entry.description,
          location: entry.location,
          is_app_visible: entry.is_app_visible,
          sort_order: entry.sort_order,
        })
        .select('id')
        .single();
      if (error) throw error;

      // Copy assignments
      const { data: assignments } = await supabase
        .from('schedule_entry_assignments')
        .select('*')
        .eq('entry_id', entry.id);
      if (assignments && assignments.length > 0) {
        await supabase.from('schedule_entry_assignments').insert(
          assignments.map((a) => ({
            entry_id: newRow.id,
            assignment_type: a.assignment_type,
            submission_id: a.submission_id,
            rule_field_key: a.rule_field_key,
            rule_operator: a.rule_operator,
            rule_value: a.rule_value,
          }))
        );
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule-entries', formId] }),
    onError: (err: Error) => notifications.show({ color: 'red', title: 'Fehler beim Duplizieren', message: err.message }),
  });

  const grouped = groupByDate(entries);

  if (grouped.length === 0) {
    return <Text c="dimmed" size="sm">Noch keine Einträge vorhanden.</Text>;
  }

  return (
    <Stack gap="md">
      {grouped.map(([date, dayEntries]) => (
        <Stack key={date} gap="xs">
          <Text fw={700} size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
            {formatDate(date)}
          </Text>
          {dayEntries
            .sort((a, b) => a.start_time.localeCompare(b.start_time))
            .map((entry) => {
              const cat = entry.category_id ? categoryMap.get(entry.category_id) : null;
              const isHidden = !entry.is_app_visible ||
                (entry.visible_until != null && entry.visible_until < new Date().toISOString().slice(0, 10));
              const isHighlighted = highlighted.has(entry.id);
              const isSelected = selected.has(entry.id);

              return (
                <Card
                  key={entry.id}
                  radius="md"
                  p="sm"
                  withBorder
                  className="contrast-surface-soft"
                  style={{
                    opacity: isHidden ? 0.55 : 1,
                    outline: isSelected
                      ? '2px solid var(--mantine-color-blue-5)'
                      : isHighlighted
                        ? '2px solid var(--mantine-color-red-5)'
                        : undefined,
                    outlineOffset: 2,
                    cursor: selectionMode ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (!selectionMode || !onToggleSelection) return;
                    onToggleSelection(entry.id);
                  }}
                >
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Group align="flex-start" gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      {selectionMode && (
                        <Checkbox
                          checked={isSelected}
                          onChange={() => onToggleSelection?.(entry.id)}
                          onClick={(event) => event.stopPropagation()}
                          mt={2}
                        />
                      )}
                      <Stack gap={0} style={{ minWidth: 56, flexShrink: 0 }}>
                        <Text size="sm" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {stripSeconds(entry.start_time)}
                        </Text>
                        <Text size="xs" c="dimmed">– {stripSeconds(entry.end_time)}</Text>
                      </Stack>
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        {cat && (
                          <Badge size="xs" color={cat.color} variant="filled" style={{ alignSelf: 'flex-start' }}>
                            {cat.icon ? `${cat.icon} ` : ''}{cat.name}
                          </Badge>
                        )}
                        <Text size="sm" fw={600} style={{ wordBreak: 'break-word' }}>{entry.title}</Text>
                        {entry.description && <Text size="xs" c="dimmed" lineClamp={2}>{entry.description}</Text>}
                        {entry.location && <Text size="xs" c="dimmed">📍 {entry.location}</Text>}
                        {entry.visible_until && <Text size="xs" c="dimmed">🕒 sichtbar bis {entry.visible_until}</Text>}
                      </Stack>
                    </Group>
                    {!selectionMode && (
                      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }} align="center">
                      <Tooltip label={entry.is_app_visible ? 'In App sichtbar' : 'In App ausgeblendet'} position="top">
                        <Switch
                          size="sm"
                          checked={entry.is_app_visible}
                          onChange={(e) => toggleVisibility.mutate({ entry, visible: e.currentTarget.checked })}
                          aria-label="In App anzeigen"
                        />
                      </Tooltip>
                      <Tooltip label="Duplizieren" position="top">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          loading={duplicateMutation.isPending}
                          onClick={() => duplicateMutation.mutate(entry)}
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon size="sm" variant="subtle" onClick={() => onEdit(entry)} title="Bearbeiten">✏️</ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => onDelete(entry)} title="Löschen">🗑</ActionIcon>
                      </Group>
                    )}
                  </Group>
                </Card>
              );
            })}
        </Stack>
      ))}
    </Stack>
  );
}
